/**
 * 3D Flood Inundation control (#177 Cesium-wow; #220 region preset + per-building
 * recolor legend) — the on-map control surface shown while the "3D Flood Inundation
 * (modelled)" layer is enabled.
 *
 * It drives the modelled water level + active demo region without re-rendering MapView:
 * the region selector, the level slider, and the "snap to live gauge" button all call
 * the floodInundationControllerRef MapView exposes (setPreset / setLevelDepth /
 * flyToDemo). The legend carries the honest framing — a modelled what-if, not an
 * official inundation product.
 */
import { useEffect, useRef, useState } from 'react';
import type { MutableRefObject } from 'react';
import type { FloodInundationController } from './MapView';
import { fetchHydrometricGauges } from './api';
import {
  FLOOD_MIN_DEPTH_M,
  FLOOD_MAX_DEPTH_M,
  FLOOD_PRESETS,
  FLOOD_DEFAULT_PRESET_ID,
  presetById,
  gaugeLevelToDepthM,
} from './floodScenario';
import {
  FLOOD_WATER_CSS,
  FLOOD_WATER_ALPHA,
  FLOOD_BUILDING_EDGE_CSS,
  FLOOD_BUILDING_FLOODED_CSS,
  FLOOD_LEGEND,
} from './styles/floodInundation';

interface Props {
  controllerRef: MutableRefObject<FloodInundationController | null>;
}

interface GaugeReadout {
  levelM: number | null;
  rateCmPerHr: number | null;
  observedAt: string;
}

export function FloodInundationControl({ controllerRef }: Props): JSX.Element {
  const [presetId, setPresetId] = useState<string>(FLOOD_DEFAULT_PRESET_ID);
  const activePreset = presetById(presetId);
  const [depthM, setDepthM] = useState<number>(activePreset.defaultDepthM);
  const [gauge, setGauge] = useState<GaugeReadout | null>(null);
  const [gaugeError, setGaugeError] = useState<string | null>(null);
  const [loadingGauge, setLoadingGauge] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  // Push the initial level on mount (the layer starts async-loading terrain in
  // parallel; setLevelDepth is stored and applied once it is ready).
  useEffect(() => {
    controllerRef.current?.setLevelDepth(depthM);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  const applyDepth = (next: number): void => {
    setDepthM(next);
    controllerRef.current?.setLevelDepth(next);
  };

  const changePreset = (nextId: string): void => {
    if (nextId === presetId) return;
    abortRef.current?.abort();
    setPresetId(nextId);
    setGauge(null);
    setGaugeError(null);
    controllerRef.current?.setPreset(nextId);
    // The layer resets to the new region's default depth; mirror it in the slider.
    const next = presetById(nextId);
    setDepthM(next.defaultDepthM);
    controllerRef.current?.setLevelDepth(next.defaultDepthM);
  };

  const snapToGauge = async (): Promise<void> => {
    const g = activePreset.gauge;
    if (!g) return;
    abortRef.current?.abort();
    const ctl = new AbortController();
    abortRef.current = ctl;
    setLoadingGauge(true);
    setGaugeError(null);
    try {
      const fc = await fetchHydrometricGauges(g.bbox, ctl.signal);
      const feature = fc.features.find((f) => f.properties.station_number === g.station);
      if (!feature) {
        setGaugeError(`Gauge ${g.station} has no recent reading.`);
        return;
      }
      const { water_level_m, rate_of_rise_cm_per_hr, observed_at } = feature.properties;
      setGauge({ levelM: water_level_m, rateCmPerHr: rate_of_rise_cm_per_hr, observedAt: observed_at });
      applyDepth(gaugeLevelToDepthM(water_level_m, g));
    } catch (err) {
      if ((err as { name?: string }).name === 'AbortError') return;
      setGaugeError('Could not reach the gauge feed.');
    } finally {
      setLoadingGauge(false);
    }
  };

  const waterSwatch = `${FLOOD_WATER_CSS}${Math.round(FLOOD_WATER_ALPHA * 255)
    .toString(16)
    .padStart(2, '0')}`;

  return (
    <div
      data-testid="flood-inundation-control"
      className="pointer-events-auto absolute bottom-14 left-3 z-[60] w-72 rounded-lg border border-slate-600 bg-slate-900/95 p-3 text-[12px] text-slate-200 shadow-xl backdrop-blur"
    >
      <div className="mb-1 flex items-center gap-2">
        <span className="text-sm">🌊</span>
        <span className="font-semibold">{FLOOD_LEGEND.title}</span>
      </div>

      <label className="mb-1 block font-medium" htmlFor="flood-preset">
        Demo region
      </label>
      <select
        id="flood-preset"
        value={presetId}
        onChange={(e) => changePreset(e.target.value)}
        className="mb-1 w-full rounded border border-slate-600 bg-slate-800 px-2 py-1 text-[12px] text-slate-100"
        aria-label="Flood demo region"
      >
        {FLOOD_PRESETS.map((p) => (
          <option key={p.id} value={p.id}>
            {p.label}
          </option>
        ))}
      </select>
      <p className="mb-2 text-[10px] leading-tight text-slate-500">{activePreset.blurb}</p>

      <button
        type="button"
        onClick={() => controllerRef.current?.flyToDemo()}
        className="mb-2 w-full rounded bg-blue-700 px-2 py-1.5 text-[12px] font-medium text-white hover:bg-blue-600"
      >
        ▶ Fly to {activePreset.label}
      </button>

      <label className="mb-1 flex items-center justify-between font-medium" htmlFor="flood-level">
        <span>Modelled flood depth</span>
        <span className="tabular-nums text-blue-300">{depthM.toFixed(1)} m</span>
      </label>
      <input
        id="flood-level"
        type="range"
        min={FLOOD_MIN_DEPTH_M}
        max={FLOOD_MAX_DEPTH_M}
        step={0.1}
        value={depthM}
        onChange={(e) => applyDepth(Number(e.target.value))}
        className="w-full accent-blue-500"
        aria-label="Modelled flood depth in metres above local ground"
      />
      <div className="mb-2 flex justify-between text-[10px] text-slate-400">
        <span>{FLOOD_MIN_DEPTH_M} m</span>
        <span>{FLOOD_MAX_DEPTH_M} m</span>
      </div>

      {activePreset.gauge && (
        <>
          <button
            type="button"
            onClick={() => void snapToGauge()}
            disabled={loadingGauge}
            className="mb-2 w-full rounded border border-slate-500 px-2 py-1.5 text-[11px] text-slate-200 hover:bg-slate-700 disabled:opacity-50"
          >
            {loadingGauge ? 'Reading gauge…' : `Snap to live gauge (${activePreset.gauge.station})`}
          </button>
          {gauge && (
            <p className="mb-2 text-[10px] text-slate-400">
              Gauge {activePreset.gauge.station}:{' '}
              {gauge.levelM !== null ? `${gauge.levelM.toFixed(2)} m (relative datum)` : 'level n/a'}
              {gauge.rateCmPerHr !== null && `, ${gauge.rateCmPerHr >= 0 ? '+' : ''}${gauge.rateCmPerHr.toFixed(1)} cm/hr`}
              {' → modelled.'}
            </p>
          )}
          {gaugeError && <p className="mb-2 text-[10px] text-amber-400">{gaugeError}</p>}
        </>
      )}

      <div className="mb-1 space-y-1 border-t border-slate-700 pt-2">
        <div className="flex items-center gap-2">
          <span
            className="inline-block h-3 w-3 shrink-0 rounded-sm"
            style={{ backgroundColor: waterSwatch }}
          />
          <span className="text-[10px]">{FLOOD_LEGEND.water}</span>
        </div>
        <div className="flex items-center gap-2">
          <span
            className="inline-block h-3 w-3 shrink-0 rounded-sm"
            style={{ backgroundColor: FLOOD_BUILDING_FLOODED_CSS }}
          />
          <span className="text-[10px]">{FLOOD_LEGEND.flooded}</span>
        </div>
        <div className="flex items-center gap-2">
          <span
            className="inline-block h-3 w-3 shrink-0 rounded-sm border-2"
            style={{ borderColor: FLOOD_BUILDING_EDGE_CSS }}
          />
          <span className="text-[10px]">{FLOOD_LEGEND.standing}</span>
        </div>
      </div>
      <p className="text-[10px] leading-tight text-slate-500">{FLOOD_LEGEND.disclaimer}</p>
    </div>
  );
}
